import React, { useEffect, useState, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { CameraIcon, CloudArrowUpIcon, PhotoIcon } from '@heroicons/react/24/outline';
import Spinner from './Spinner';
import './AvatarSelectionModal.css';

// Props: backendService, onSelect(file, url), close, clerkAvatarUrl, isUsingCustomAvatar, onUseAccountPicture
const AvatarSelectionModal = ({ backendService, onSelect, close, clerkAvatarUrl, isUsingCustomAvatar, onUseAccountPicture }) => {
  const [userMedia, setUserMedia] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [takingPhoto, setTakingPhoto] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [processingAction, setProcessingAction] = useState(null); // 'selecting' | 'camera' | 'uploading' | 'account' | null
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const activeRef = useRef(true);
  useEffect(() => {
    activeRef.current = true;
    return () => { activeRef.current = false; };
  }, []);

  // Handler for using account picture
  const handleUseAccountPicture = async () => {
    if (processingAction || !onUseAccountPicture) return;
    setProcessingAction('account');
    try {
      await onUseAccountPicture();
      close();
    } catch (err) {
      console.error('Error using account picture:', err);
      setProcessingAction(null);
    }
  };

  const normalizeActivities = (res) => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.activities)) return res.activities;
    if (Array.isArray(res.data)) return res.data;
    return [];
  };

  const fetchUserMedia = async (page = 1) => {
    try {
      const limit = 20;
      const mediaActivities = await backendService.fetchActivities(page, limit, 'media');
      const activities = normalizeActivities(mediaActivities);
      
      if (activities.length === 0) {
        setHasMore(false);
        setInitialLoading(false);
        return;
      }

      const mediaOnly = activities.filter(a => a && a.type === 'media');
      
      // Flatten files
      const flattened = [];
      mediaOnly.forEach((activity) => {
        const files = (activity.data && Array.isArray(activity.data.files)) ? activity.data.files : [];
        files.forEach((file) => {
          flattened.push({
            activityId: activity._id || activity.id,
            fileId: file._id || file.id,
          });
        });
      });

      if (flattened.length === 0) {
        if (activities.length < limit) {
          setHasMore(false);
          setInitialLoading(false);
        } else {
          fetchUserMedia(page + 1); // Recursively fetch if this page had no media files
        }
        return;
      }

      const withThumbs = await Promise.all(flattened.map(async (entry) => {
        try {
          const thumbUrl = await backendService.fetchMediaFileUrl(entry.activityId, entry.fileId, '256px');
          return { ...entry, thumbnail_url: thumbUrl };
        } catch (err) {
          console.warn('Failed to load media thumbnail', err);
          return { ...entry, thumbnail_url: null };
        }
      }));

      setUserMedia(prev => [...prev, ...withThumbs]);
      setCurrentPage(page);
      if (activities.length < limit) setHasMore(false);
      setInitialLoading(false);
      
    } catch (err) {
      console.error('Failed to fetch user media:', err);
      setHasMore(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    if (showHistory && userMedia.length === 0) {
      setInitialLoading(true);
      fetchUserMedia(1);
    }
    
    return () => {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(t => t.stop());
        mediaStreamRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHistory]);

  const handleSelectExisting = async (media) => {
    if (processingAction) return; // Prevent double-clicks
    setProcessingAction('selecting');
    try {
      console.log('[avatar] selecting existing media', media);
      const activityId = media.activityId || media.activity_id || media.id;
      const fileId = media.fileId || media.file_id;
      const blob = await backendService.fetchMediaFileBlob(activityId, fileId, 'raw');
      if (!activeRef.current) return;
      if (!blob) throw new Error('No file data returned');
      const file = new File([blob], `avatar_from_media_${Date.now()}.png`, { type: blob.type || 'image/png' });
      let url;
      try {
        url = URL.createObjectURL(blob);
      } catch (err) {
        // fallback: create blob URL from file
        url = URL.createObjectURL(new Blob([blob], { type: blob.type || 'image/png' }));
      }
      console.log('[avatar] prepared file from existing media', { size: file.size, type: file.type });
      close();
      onSelect && onSelect(file, url);
    } catch (err) {
      console.error('Failed to load media file for avatar:', err);
      alert('Failed to load selected media file');
      setProcessingAction(null);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    // Cancelling the OS file picker fires change with no file. Previously this
    // did nothing, but if a prior attempt had set processingAction the modal
    // stayed stuck in a busy state with no way to recover.
    if (!f) {
      setProcessingAction(null);
      return;
    }
    if (f.type && !f.type.startsWith('image/')) {
      setProcessingAction(null);
      alert('Please choose an image file.');
      return;
    }
    if (f.size === 0) {
      setProcessingAction(null);
      alert('That file appears to be empty. Please choose a different image.');
      return;
    }
    setProcessingAction('uploading');
    const url = URL.createObjectURL(f);
    close();
    onSelect && onSelect(f, url);
  };

  const startCamera = async () => {
    if (processingAction) return;
    setProcessingAction('camera');
    setShowHistory(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (!activeRef.current) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      mediaStreamRef.current = stream;
      setTakingPhoto(true);
      setProcessingAction(null);
    } catch (err) {
      console.error('Error accessing camera:', err);
      alert('Unable to access camera');
      setProcessingAction(null);
    }
  };

  useEffect(() => {
    if (takingPhoto && videoRef.current && mediaStreamRef.current) {
      videoRef.current.srcObject = mediaStreamRef.current;
      videoRef.current.play().catch(e => console.error("Error playing video:", e));
    }
  }, [takingPhoto]);

  const stopCamera = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }
    setTakingPhoto(false);
  };

  const handleHistoryClick = () => {
    if (showHistory) {
      setShowHistory(false);
    } else {
      stopCamera();
      setShowHistory(true);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      console.warn("Video dimensions are zero, cannot capture.");
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    canvas.toBlob((blob) => {
      if (!blob) {
        console.error("Failed to create blob from canvas");
        return;
      }
      const file = new File([blob], `avatar_${Date.now()}.png`, { type: 'image/png' });
      const url = URL.createObjectURL(blob);
      stopCamera();
      close();
      onSelect && onSelect(file, url);
    }, 'image/png');
  };

  return (
    <div className="avatar-selection-modal">
      <div className="avatar-selection-header">
        <h2>Choose Profile Photo</h2>
      </div>

      {/* Loading overlay when processing an action */}
      {processingAction && (
        <div className="avatar-processing-overlay">
          <Spinner />
          <span className="avatar-loading-text">
            {processingAction === 'selecting' && 'Loading photo...'}
            {processingAction === 'camera' && 'Starting camera...'}
            {processingAction === 'uploading' && 'Preparing photo...'}
            {processingAction === 'account' && 'Switching to account picture...'}
          </span>
        </div>
      )}

      {/* Use Account Picture - shown when user has a custom avatar and account picture is available */}
      {clerkAvatarUrl && (
        <div className={`avatar-account-row ${processingAction ? 'disabled' : ''}`}>
          <button 
            className={`avatar-account-btn ${!isUsingCustomAvatar ? 'current' : ''}`}
            onClick={handleUseAccountPicture}
            disabled={!isUsingCustomAvatar || !!processingAction}
            title={isUsingCustomAvatar ? 'Switch to your sign-in account picture' : 'Currently using account picture'}
          >
            <img 
              src={clerkAvatarUrl} 
              alt="Account" 
              className="avatar-account-preview"
            />
            <div className="avatar-account-info">
              <span className="avatar-account-label">Account Picture</span>
              <span className="avatar-account-hint">
                {isUsingCustomAvatar ? 'Click to use this instead' : '✓ Currently in use'}
              </span>
            </div>
          </button>
        </div>
      )}

      <div className={`avatar-selection-actions ${processingAction ? 'disabled' : ''}`}>
        <label className="avatar-action-btn">
          <CloudArrowUpIcon />
          <span>Upload</span>
          <input 
            type="file" 
            accept="image/*" 
            onChange={handleFileChange} 
            style={{ display: 'none' }}
            disabled={!!processingAction}
          />
        </label>
        
        <button 
          className={`avatar-action-btn ${takingPhoto ? 'active' : ''}`} 
          onClick={takingPhoto ? stopCamera : startCamera}
          disabled={!!processingAction}
        >
          <CameraIcon />
          <span>Camera</span>
        </button>

        <button 
          className={`avatar-action-btn ${showHistory ? 'active' : ''}`} 
          onClick={handleHistoryClick}
          disabled={!!processingAction}
        >
          <PhotoIcon />
          <span>History</span>
        </button>
      </div>

      {takingPhoto && (
        <div className="camera-container">
          <video ref={videoRef} className="camera-preview" playsInline muted></video>
          <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
          <div className="camera-controls">
            <button className="btn-cancel" onClick={stopCamera}>Cancel</button>
            <button className="btn-save" onClick={capturePhoto}>Capture</button>
          </div>
        </div>
      )}

      {showHistory && (
        <div className="avatar-history-section">
          <h3>Recent Photos</h3>
          {initialLoading ? (
            <div className="avatar-loading-container">
              <Spinner />
              <span className="avatar-loading-text">Loading recent photos...</span>
            </div>
          ) : (
            <div id="avatar-scroll-target" className="avatar-scroll-container">
              <InfiniteScroll
                dataLength={userMedia.length}
                next={() => fetchUserMedia(currentPage + 1)}
                hasMore={hasMore}
                loader={<div className="avatar-loading-container"><Spinner /><span className="avatar-loading-text">Loading...</span></div>}
                scrollableTarget="avatar-scroll-target"
                className="avatar-grid"
              >
                {userMedia.map((media, idx) => (
                  <div 
                    key={`${media.activityId}-${media.fileId}-${idx}`} 
                    className="avatar-grid-item"
                    onClick={() => handleSelectExisting(media)}
                  >
                    {media.thumbnail_url ? (
                      <img src={media.thumbnail_url} alt="User media" />
                    ) : (
                      <div className="avatar-grid-placeholder">
                        <PhotoIcon />
                      </div>
                    )}
                  </div>
                ))}
                {userMedia.length === 0 && !hasMore && (
                  <div className="no-media-message">No recent photos found</div>
                )}
              </InfiniteScroll>
            </div>
          )}
        </div>
      )}

      <div className="button-container">
        <button className="btn-cancel" onClick={close}>Cancel</button>
      </div>
    </div>
  );
};

export default AvatarSelectionModal;
